/**
 * Shared NDJSON stream parser for chat composables. Reads a streaming response and dispatches
 * events from the frozen chat wire contract (`shared/chat/wireEvents.ts`) — the single source of
 * truth both engines emit and this parser consumes (handoff §7.7).
 */
import type { NdjsonEvent, WireQuickResearchActivityStep } from '../../shared/chat/wireEvents'
import { readJsonLines } from '#shared/stream/jsonLines'
import { parseUsageLimitResponse, type UsageLimitResponse } from '#shared/usage/usageLimit'

/**
 * The client's consumption view of a legal-citation preview. Structurally the wire `WireCitation`
 * plus the store-required `normTitle` (see the note in `wireEvents.ts`); citations are cast to this
 * at the consumption edge below.
 */
export interface CitationData {
  sectionId: string
  normId: string
  sectionNumber?: string | null
  displayRef?: string | null
  normTitle: string
  label: string
  snippet: string
  fullText?: string
}

export interface StreamCallbacks {
  onDelta: (content: string) => void
  // ADK engine only: the authoritative full text of the current assistant run. Replaces whatever the
  // display-only deltas accumulated for this run (the agentloop engine never emits it).
  onFinal?: (content: string) => void
  /**
   * The model is thinking (MAR-1020). The event carries no text; callers with nowhere to show a
   * "Pensando…" simply leave this out.
   */
  onThinking?: () => void
  // `id` is the ADK function-call id — the browser echoes it back in its functionResponse to resume
  // the paused session.
  onTool: (name: string, input: Record<string, unknown>, id?: string) => void | Promise<void>
  onStatus: (message: string, code?: string, params?: Record<string, unknown>) => void
  onToolResult?: (name: string, items: string[], citations?: Record<string, CitationData>, docNames?: Record<string, string>, activity?: WireQuickResearchActivityStep[], result?: unknown) => void
  /** The server refused a client tool call after its card was already emitted (MAR-97). */
  onToolRejected?: (name: string, reason: string, id?: string) => void
  onToolCalls?: (calls: unknown[]) => void
  /** A server tool returned (MAR-1057); `id` is its function-call id. */
  onToolDone?: (name: string, id?: string) => void
  onIterationStart?: () => void
  onValidationStart?: (payload: { validator: string }) => void
  onValidationResult?: (payload: { validator: string; result: unknown }) => void
  onSubagentEvent?: (payload: { agent: string; toolCallId: string; event: unknown }) => void
  /**
   * The gateway refused the organization (MAR-1009) — the `429 USAGE_LIMIT_REACHED` body, as an event
   * because the stream was already open. Callers hand it to `useUsageLimit().report`.
   */
  onUsageLimit?: (block: UsageLimitResponse) => void
}

/**
 * Read an NDJSON stream and dispatch parsed events via callbacks.
 * Supports abort via the signal parameter.
 */
export async function readNdjsonStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  callbacks: StreamCallbacks,
  signal?: AbortSignal,
): Promise<void> {
  await readJsonLines(reader, async (value) => {
    // A callback's own error never ends the stream: the loop this replaced wrapped parse and dispatch
    // in one `try`, so a throwing callback cost its one event and the turn read on. Kept on purpose.
    try {
      await dispatchWireEvent(value, callbacks)
    } catch {
      // Skip an event a callback could not apply.
    }
  }, { signal })
}

/**
 * Apply ONE chat wire event to the callbacks — the single place an event is routed to its handler.
 *
 * The NDJSON reader above calls it per line, and a queued Slow turn (MAR-1390,
 * `web-chat/streamQueuedChatJob.ts`) calls it per entry of its Firestore job's `events`: the same
 * events, the same routing, the same handlers. A queued turn must not grow a second mapping of what an
 * event means.
 */
export async function dispatchWireEvent(value: unknown, callbacks: StreamCallbacks): Promise<void> {
  const ev = value as NdjsonEvent
  // Citations cross the wire as WireCitation; the store consumes them as CitationData (see note in
  // wireEvents.ts) — cast at this single consumption edge.
  const asCitations = (c: unknown) => c as Record<string, CitationData> | undefined
  if (ev.type === 'delta') {
    callbacks.onDelta(ev.content)
  } else if (ev.type === 'final') {
    callbacks.onFinal?.(ev.content)
  } else if (ev.type === 'thinking') {
    callbacks.onThinking?.()
  } else if (ev.type === 'tool') {
    await callbacks.onTool(ev.name, ev.input, ev.id)
  } else if (ev.type === 'tool_rejected') {
    callbacks.onToolRejected?.(ev.name, ev.reason, ev.id)
  } else if (ev.type === 'tool_result') {
    callbacks.onToolResult?.(ev.name, ev.items, asCitations(ev.citations), ev.docNames, ev.activity, ev.result)
  } else if (ev.type === 'tool_calls') {
    callbacks.onToolCalls?.(ev.calls)
  } else if (ev.type === 'tool_done') {
    callbacks.onToolDone?.(ev.name, ev.id)
  } else if (ev.type === 'status') {
    callbacks.onStatus(ev.message || '', ev.code, ev.params)
  } else if (ev.type === 'iteration_start') {
    callbacks.onIterationStart?.()
  } else if (ev.type === 'validation_start') {
    callbacks.onValidationStart?.({ validator: ev.validator })
  } else if (ev.type === 'validation_result') {
    callbacks.onValidationResult?.({ validator: ev.validator, result: ev.result })
  } else if (ev.type === 'usage_limit') {
    const block = parseUsageLimitResponse(ev)
    if (block) callbacks.onUsageLimit?.(block)
  } else if (ev.type === 'subagent_event') {
    callbacks.onSubagentEvent?.({ agent: ev.agent, toolCallId: ev.toolCallId, event: ev.event })
    const nested = ev.event
    if (nested?.type === 'tool_result') {
      callbacks.onToolResult?.(nested.name, nested.items || [], asCitations(nested.citations))
    } else if (nested?.type === 'validation_start') {
      callbacks.onValidationStart?.({ validator: nested.validator })
    } else if (nested?.type === 'validation_result') {
      callbacks.onValidationResult?.({ validator: nested.validator, result: nested.result })
    }
  }
}

/**
 * Read a raw text stream (no NDJSON framing) and dispatch all content as deltas.
 */
export async function readTextStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  onDelta: (content: string) => void,
): Promise<void> {
  const decoder = new TextDecoder()
  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    onDelta(decoder.decode(value, { stream: true }))
  }
}

/**
 * A client-minted id for a chat message or conversation — a **uuid**, because these are sent to the
 * server and stored as the row's primary key (`conversation_messages.id`, `agent_sessions.id`, both
 * `uuid` columns). Minting it here means the message has ONE identity from the moment it exists,
 * instead of a local id that the DB later disagrees with; that disagreement is what forced the edit
 * flow to key on timestamps and the ADK session to guess where a conversation branched (MAR-41).
 *
 * The non-uuid fallback is for a runtime without `crypto.randomUUID` (pre-2021 browsers, or an
 * insecure origin). It is display-only — a server insert with it would be rejected by the uuid column.
 */
export function generateId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  } catch {
    // fall through
  }
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7)
}

export function windowMessages<T>(messages: T[], limit: number): T[] {
  if (messages.length <= limit) return messages
  return messages.slice(-limit)
}
